import { useTranslation } from "react-i18next";
import { CalendarPlus, Trash2 } from "lucide-react";
import type { Event2Label } from "@wedding/shared";

/**
 * Opsiyonel ikinci etkinlik (kına / nikah / after-party) form bölümü.
 *
 * Organizer ve admin panellerinin düğün bilgileri formu birebir aynı olmak
 * zorunda (Admin ⇄ Organizer eşitliği) — blok iki dosyaya kopyalanmak yerine
 * burada TEK yerde duruyor, iki panel de aynı bileşeni render ediyor. Yetki
 * ayrımı çağıranın kendi endpoint'inde.
 *
 * Kontrollü bileşen: `value === null` → etkinlik tanımlı değil, yalnız "Ekle"
 * butonu görünür. `onChange(null)` → kaldır (kaydedilince kolonlar NULL olur).
 */

export interface SecondEventValue {
  /** `<input type="datetime-local">` değeri — ana düğün tarihiyle aynı konvansiyon. */
  dateTime: string;
  location: string;
  /** `""` = henüz seçilmedi (kaydetmeden önce doğrulama yakalar). */
  label: Event2Label | "";
  /** Yalnız `label === "other"` iken anlamlı. */
  labelCustom: string;
}

export const EMPTY_SECOND_EVENT: SecondEventValue = {
  dateTime: "",
  location: "",
  label: "kina",
  labelCustom: "",
};

const LABEL_OPTIONS: Event2Label[] = ["kina", "nikah", "dugun", "after_party", "other"];

/** API satırı (snake_case) → form değeri. Tarih yoksa etkinlik tanımlı değildir. */
export function secondEventFromWedding(w: {
  event2_date_time?: string | null;
  event2_location?: string | null;
  event2_label?: Event2Label | null;
  event2_label_custom?: string | null;
} | null | undefined): SecondEventValue | null {
  if (!w?.event2_date_time) return null;
  return {
    dateTime: w.event2_date_time.slice(0, 16),
    location: w.event2_location ?? "",
    label: w.event2_label ?? "kina",
    labelCustom: w.event2_label_custom ?? "",
  };
}

/** Form değeri → `PATCH /info` gövdesi. `null` alanlar kolonu temizler. */
export function secondEventPayload(v: SecondEventValue | null): {
  event2DateTime: string | null;
  event2Location: string | null;
  event2Label: Event2Label | null;
  event2LabelCustom: string | null;
} {
  if (!v || !v.dateTime || !v.label) {
    return { event2DateTime: null, event2Location: null, event2Label: null, event2LabelCustom: null };
  }
  return {
    event2DateTime: v.dateTime,
    event2Location: v.location.trim() || null,
    event2Label: v.label,
    // Sunucu "other değilken custom dolu olamaz" diyor — burada da temizle.
    event2LabelCustom: v.label === "other" ? v.labelCustom.trim() : null,
  };
}

/**
 * Kaydetmeden önceki istemci doğrulaması — sunucudaki `superRefine`'ın erken
 * UX karşılığı. Hata varsa i18n anahtarı döner, yoksa `null`.
 */
export function validateSecondEvent(
  v: SecondEventValue | null,
): "wedding.event2.dateRequired" | "wedding.event2.customLabelRequired" | null {
  if (!v) return null;
  if (!v.dateTime) return "wedding.event2.dateRequired";
  if (v.label === "other" && !v.labelCustom.trim()) return "wedding.event2.customLabelRequired";
  return null;
}

/**
 * Dirty-check için kararlı karşılaştırma anahtarı.
 *
 * ⚠️ `secondEventPayload` DEĞİL, ham form değeri üzerinden: bölümü açıp
 * tarihi boş bırakmak payload'da "hepsi null"a çöküyordu → form "temiz"
 * görünüp Kaydet pasif kalıyor, kullanıcı eksik alan uyarısını hiç alamıyordu.
 */
export function secondEventKey(v: SecondEventValue | null): string {
  if (!v) return "none";
  return JSON.stringify([
    v.dateTime,
    v.location.trim(),
    v.label,
    v.label === "other" ? v.labelCustom.trim() : "",
  ]);
}

const inputCls =
  "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20";
const labelCls =
  "text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block";

/**
 * Ana etkinliğin türü (migration 051).
 *
 * Neden gerekti: 050 yalnız ikinci etkinliğe etiket verdi, ana etkinlik her
 * yerde sabit "Düğün" yazılıyordu. Gerçek kullanımda ana tarih KINA oldu ve
 * kınaya "Düğün" dendi. Bu alanla "hangi tarih ana" sorusu anlamsızlaşır —
 * her etkinlik kendi adını taşır, sayfa ikisini kronolojik gösterir.
 * Boş bırakılırsa eski davranış ("Düğün") sürer.
 */
export function MainEventTypeField({
  label,
  labelCustom,
  onChange,
  disabled = false,
}: {
  label: Event2Label | "";
  labelCustom: string;
  onChange: (next: { label: Event2Label | ""; labelCustom: string }) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  function optionLabel(opt: Event2Label): string {
    switch (opt) {
      case "kina": return t("wedding.event2Label.kina");
      case "nikah": return t("wedding.event2Label.nikah");
      case "dugun": return t("wedding.event2Label.dugun");
      case "after_party": return t("wedding.event2Label.afterParty");
      case "other": return t("wedding.event2Label.other");
    }
  }

  return (
    <div>
      <label htmlFor="event1-label" className={labelCls}>
        {t("wedding.program.mainEventTypeLabel")}
      </label>
      <select
        id="event1-label"
        value={label}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value as Event2Label | "";
          onChange({ label: next, labelCustom: next === "other" ? labelCustom : "" });
        }}
        className={`${inputCls} cursor-pointer`}
      >
        <option value="">{t("wedding.event2Label.dugun")}</option>
        {LABEL_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>{optionLabel(opt)}</option>
        ))}
      </select>

      {label === "other" && (
        <input
          type="text"
          value={labelCustom}
          disabled={disabled}
          onChange={(e) => onChange({ label, labelCustom: e.target.value })}
          placeholder={t("wedding.event2.customLabelPlaceholder")}
          aria-label={t("wedding.event2.customLabelPlaceholder")}
          maxLength={80}
          className={`${inputCls} mt-2`}
        />
      )}
    </div>
  );
}

/** Ana etkinlik etiketi → `PATCH /info` gövdesi. */
export function mainEventPayload(label: Event2Label | "", labelCustom: string): {
  event1Label: Event2Label | null;
  event1LabelCustom: string | null;
} {
  if (!label) return { event1Label: null, event1LabelCustom: null };
  return {
    event1Label: label,
    event1LabelCustom: label === "other" ? labelCustom.trim() || null : null,
  };
}

/** Ana etkinlik için istemci doğrulaması. */
export function validateMainEvent(
  label: Event2Label | "",
  labelCustom: string,
): "wedding.event2.customLabelRequired" | null {
  return label === "other" && !labelCustom.trim() ? "wedding.event2.customLabelRequired" : null;
}

export interface SecondEventFieldsProps {
  value: SecondEventValue | null;
  onChange: (value: SecondEventValue | null) => void;
  disabled?: boolean;
}

export function SecondEventFields({ value, onChange, disabled = false }: SecondEventFieldsProps) {
  const { t } = useTranslation();

  // ⚠️ Literal switch — `t(MAP[label])` yazılsaydı `check:i18n-keys` bu
  // anahtarları sessizce atlardı (dinamik çağrıları doğrulamıyor).
  function optionLabel(label: Event2Label): string {
    switch (label) {
      case "kina": return t("wedding.event2Label.kina");
      case "nikah": return t("wedding.event2Label.nikah");
      case "dugun": return t("wedding.event2Label.dugun");
      case "after_party": return t("wedding.event2Label.afterParty");
      case "other": return t("wedding.event2Label.other");
    }
  }

  if (!value) {
    return (
      <div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(EMPTY_SECOND_EVENT)}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary/50 hover:text-primary disabled:opacity-50 transition-colors cursor-pointer"
        >
          <CalendarPlus className="h-4 w-4" />
          {t("wedding.event2.addButton")}
        </button>
        <p className="mt-2 text-xs text-muted-foreground">{t("wedding.event2.sectionHint")}</p>
      </div>
    );
  }

  const set = (patch: Partial<SecondEventValue>) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-2xl border border-border bg-muted/30 p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{t("wedding.event2.sectionTitle")}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("wedding.event2.sectionHint")}</p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors cursor-pointer"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {t("wedding.event2.removeButton")}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="event2-date" className={labelCls}>
            {t("wedding.event2.dateTimeLabel")}
          </label>
          <input
            id="event2-date"
            type="datetime-local"
            value={value.dateTime}
            disabled={disabled}
            onChange={(e) => set({ dateTime: e.target.value })}
            className={inputCls}
          />
        </div>

        <div>
          <label htmlFor="event2-label" className={labelCls}>
            {t("wedding.event2.labelLabel")}
          </label>
          <select
            id="event2-label"
            value={value.label}
            disabled={disabled}
            onChange={(e) => set({ label: e.target.value as Event2Label })}
            className={`${inputCls} cursor-pointer`}
          >
            {LABEL_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{optionLabel(opt)}</option>
            ))}
          </select>
        </div>
      </div>

      {value.label === "other" && (
        <div>
          <label htmlFor="event2-label-custom" className={labelCls}>
            {t("wedding.event2.customLabelPlaceholder")}
          </label>
          <input
            id="event2-label-custom"
            type="text"
            value={value.labelCustom}
            disabled={disabled}
            onChange={(e) => set({ labelCustom: e.target.value })}
            placeholder={t("wedding.event2.customLabelPlaceholder")}
            maxLength={80}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-muted-foreground text-right">{value.labelCustom.length} / 80</p>
        </div>
      )}

      <div>
        <label htmlFor="event2-location" className={labelCls}>
          {t("wedding.event2.locationLabel")}
        </label>
        <input
          id="event2-location"
          type="text"
          value={value.location}
          disabled={disabled}
          onChange={(e) => set({ location: e.target.value })}
          placeholder={t("wedding.event2.locationPlaceholder")}
          maxLength={500}
          className={inputCls}
        />
        <p className="mt-1 text-xs text-muted-foreground text-right">{value.location.length} / 500</p>
      </div>
    </div>
  );
}
